import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { CalendarIcon, ClockIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import util from "utils/utils";

import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";

import { getDateStatus } from "../dateStatus";
import HomeBlock from "./HomeBlock";

const ProjectAssignedIdeasBlock = () => {
  const theme = useTheme();
  const [ideaPreviewId, setIdeaPreviewId] = useState("");

  const dashboardQuery = useQuery(initUseQuery(api.users.dashboard, []));

  const assignments = dashboardQuery.data?.projectAssignments ?? [];

  if (!assignments.length) return null;

  return (
    <>
      <HandledIdeaPreview
        ideaId={ideaPreviewId || null}
        onCloseModal={() => setIdeaPreviewId("")}
        redirectToBoard={true}
      />
      <HomeBlock>
        <H3>
          Your project assignments
          <Subheader>
            {assignments.length} project{" "}
            {assignments.length === 1 ? "assignment" : "assignments"} assigned
            to you.
          </Subheader>
        </H3>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 12,
            overflowX: "auto",
            width: 0,
            minWidth: "100%",
            padding: 12,
          }}
        >
          {assignments.map((idea) => {
            const board = idea.projectManagement?.boards?.find(
              (entry) => entry.forId === idea.challenge,
            );
            const deadlineStatus = getDateStatus(board?.deadline);
            const reviewStatus = getDateStatus(board?.reviewDate);

            return (
              <div key={idea._id} style={{ width: 200, display: "flex" }}>
                <Card style={{ margin: 0, flex: 1 }} elevation="small">
                  <Image
                    src={util.ideaCoverImage(idea, "200x200")}
                    height={100}
                    style={{
                      objectFit: "cover",
                      backgroundColor: theme.colors.primary.soft,
                    }}
                  />
                  <CardContent style={{ flex: 1 }}>
                    <H4>
                      {idea.name}
                      <Subheader>
                        {idea.challengeName}
                        <br />
                        Stage: <b>{idea.projectLane || "Default"}</b>
                      </Subheader>
                    </H4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        marginTop: 8,
                      }}
                    >
                      {deadlineStatus ? (
                        <Label
                          color={deadlineStatus.color}
                          size="small"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon icon={CalendarIcon} />
                          Deadline: {deadlineStatus.text}
                        </Label>
                      ) : null}
                      {reviewStatus ? (
                        <Label
                          color={reviewStatus.color}
                          size="small"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon icon={ClockIcon} />
                          Review: {reviewStatus.text}
                        </Label>
                      ) : null}
                    </div>
                  </CardContent>
                  <CardContent
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      flex: 0,
                    }}
                  >
                    <Button
                      color="primary"
                      variant="outline"
                      size="small"
                      onClick={() => setIdeaPreviewId(idea._id)}
                    >
                      View
                    </Button>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </HomeBlock>
    </>
  );
};

export default ProjectAssignedIdeasBlock;
